// ─────────────────────────────────────────────────────────────────────────────
// CookieModal.tsx
// Hoy Toca Recetas — Grupo Herdez
//
// Modal de configuración granular de cookies.
// Se abre desde el CookieBanner ("Configurar") o desde el footer ("Gestionar cookies").
// Las cookies técnicas aparecen bloqueadas (Switch disabled).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Link } from 'react-router-dom';
import { useConsent } from '../../hooks/useConsent';

// ── Definición de categorías ──────────────────────────────────────────────────

interface Category {
  id:       'technical' | 'analytics' | 'marketing';
  label:    string;
  desc:     string;
  locked:   boolean;
  examples: string;
}

const CATEGORIES: Category[] = [
  {
    id:       'technical',
    label:    'Cookies técnicas (necesarias)',
    desc:     'Permiten la navegación y el uso de funciones esenciales como el inicio de sesión, el carrito de favoritos y las preferencias de idioma. No pueden desactivarse.',
    locked:   true,
    examples: 'Sesión de usuario, preferencias de idioma, seguridad CSRF.',
  },
  {
    id:       'analytics',
    label:    'Cookies analíticas',
    desc:     'Nos ayudan a entender cómo interactúas con la plataforma: qué recetas visitas, cuánto tiempo permaneces en cada página y desde qué dispositivos accedes. Toda la información es anónima.',
    locked:   false,
    examples: 'Google Analytics 4, Hotjar.',
  },
  {
    id:       'marketing',
    label:    'Cookies de marketing',
    desc:     'Permiten mostrarte contenido personalizado y promociones relevantes de Grupo Herdez dentro y fuera de la plataforma, basándonos en tus intereses.',
    locked:   false,
    examples: 'Meta Pixel, Google Ads.',
  },
];

// ── Props ─────────────────────────────────────────────────────────────────────

interface CookieModalProps {
  open:    boolean;
  onClose: () => void;
}

// ── Componente ────────────────────────────────────────────────────────────────

export const CookieModal: React.FC<CookieModalProps> = ({ open, onClose }) => {
  const { consent, savePartial, rejectAll, acceptAll } = useConsent();

  // Estado local: copia editable hasta que el usuario guarde
  const [analytics, setAnalytics] = useState(consent.analytics);
  const [marketing, setMarketing] = useState(consent.marketing);

  // Sincronizar si el consent externo cambia (p.ej. "Aceptar todo" desde el banner)
  useEffect(() => {
    setAnalytics(consent.analytics);
    setMarketing(consent.marketing);
  }, [consent.analytics, consent.marketing]);

  const handleSave = () => {
    savePartial(analytics, marketing);
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAll();
    onClose();
  };

  const handleRejectAll = () => {
    rejectAll();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="cookie-modal-title"
    >
      {/* ── Título ── */}
      <DialogTitle id="cookie-modal-title" sx={{ pb: 1 }}>
        Configuración de cookies
      </DialogTitle>

      {/* ── Contenido ── */}
      <DialogContent dividers sx={{ p: 0 }}>
        {CATEGORIES.map((cat, index) => (
          <Box key={cat.id}>
            {index > 0 && <Divider />}

            <Box sx={{ px: 3, py: 2.5 }}>
              {/* Cabecera de categoría */}
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      cat.id === 'analytics' ? analytics
                      : cat.id === 'marketing' ? marketing
                      : true  // technical siempre on
                    }
                    disabled={cat.locked}
                    onChange={(e) => {
                      if (cat.id === 'analytics') setAnalytics(e.target.checked);
                      if (cat.id === 'marketing') setMarketing(e.target.checked);
                    }}
                    inputProps={{ 'aria-label': cat.label }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {cat.locked && (
                      <LockOutlinedIcon
                        sx={{ fontSize: 15, color: 'text.disabled' }}
                        aria-hidden="true"
                      />
                    )}
                    <Typography variant="body2" fontWeight={500}>
                      {cat.label}
                    </Typography>
                  </Box>
                }
                labelPlacement="start"
                sx={{
                  width:          '100%',
                  justifyContent: 'space-between',
                  ml:             0,
                  mb:             1,
                }}
              />

              {/* Descripción */}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {cat.desc}
              </Typography>

              {/* Ejemplos */}
              <Typography variant="caption" color="text.disabled">
                Ejemplos: {cat.examples}
              </Typography>
            </Box>
          </Box>
        ))}

        {/* ── Enlace al aviso de privacidad ── */}
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Puedes cambiar estas preferencias en cualquier momento.{' '}
            Consulta nuestro{' '}
            <Link
              to="/Privacy"
              style={{ color: 'inherit', fontWeight: 600 }}
              onClick={onClose}
            >
              Aviso de Privacidad
            </Link>{' '}
            para más información.
          </Typography>
        </Box>
      </DialogContent>

      {/* ── Acciones ── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          onClick={handleRejectAll}
          sx={{ mr: 'auto' }}
        >
          Rechazar no esenciales
        </Button>
        <Button size="small" variant="outlined" onClick={handleSave}>
          Guardar preferencias
        </Button>
        <Button size="small" variant="contained" onClick={handleAcceptAll}>
          Aceptar todas
        </Button>
      </DialogActions>
    </Dialog>
  );
};
